/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/services/api/apiWrapper.ts
 * PURPOSE: Business logic service layer implementation
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance - Rule 7: API Contract)
 * SPRINT GOVERNANCE: Rule 7: Performance SLAs (<50ms p95)
 *
 * CTO MANDATE: Production-ready React component with enterprise quality standards
 *
 * @business-context
 * - Domain: API Integration, Backend Communication, Vietnamese F&B Data Layer
 * - Impact: Data Consistency, API Performance, Error Handling
 * - Stakeholders: Development Team, Backend Team, Operations
 *
 * @technical-architecture
 * - Layer: Data Access Layer, API Services
 * - Pattern: Service Pattern, HTTP Client, Error Handling
 * - Dependencies: Axios, TypeScript, API Contracts
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/
 * - API: docs/03-integrate/01-API-Specifications/
 *  */

import { API_ENDPOINTS, FEATURES, isEndpointAvailable } from '../../config/endpoints';
// Production API imports only

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  // Production response format
}

/**
 * The server's message from any backend error body: {detail: string},
 * {detail: {message}}, {error: {message}} (error_handler.py, where message may
 * itself be a {message} dict) or {message}. Null when there is none.
 */
function serverMessage(body: any): string | null {
  for (const m of [body?.detail, body?.error?.message, body?.message]) {
    if (typeof m === 'string' && m) return m;
    if (typeof m?.message === 'string' && m.message) return m.message;
  }
  return null;
}

class ApiWrapper {
  private baseUrl = '/api/v1';

  // Kept for backward-compat call sites — no-ops since credentials are read
  // from localStorage on every request (same pattern as client.ts interceptor).
  setToken(_token: string | null) { /* no-op: read from localStorage per-request */ }
  setTenantKey(_tenantKey: string | null) { /* no-op: read from localStorage per-request */ }

  /** Read access token from persisted localStorage — survives page reload. */
  private getAccessToken(): string | null {
    try {
      return typeof localStorage !== 'undefined' ? localStorage.getItem('access_token') : null;
    } catch { return null; }
  }

  /** Read tenant key from persisted Zustand auth-storage — survives page reload. */
  private getTenantKey(): string | null {
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('auth-storage') : null;
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.state?.tenantKey || null;
    } catch { return null; }
  }

  private async makeRequest<T>(
    endpoint: string | null,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    // Production endpoint validation
    if (!endpoint || !isEndpointAvailable(endpoint)) {
      return {
        success: false,
        error: 'Endpoint not available',
        message: 'Tính năng này chưa được triển khai',
      };
    }

    // Set default headers — cast options.headers to plain object (HeadersInit is a union type)
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> | undefined),
    };

    // Read credentials from localStorage on every request (same as client.ts interceptor).
    // This ensures correct credentials after page reloads without requiring setToken/setTenantKey calls.
    const token = this.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      // Only send X-Tenant-Key when authenticated (tenant_user_access tokens require it)
      const tenantKey = this.getTenantKey();
      if (tenantKey) {
        headers['X-Tenant-Key'] = tenantKey;
      }
    }

    // #1317: only a fetch() rejection (no HTTP response at all) is reported as
    // 'Network error' — callers queue offline on that value alone. Any HTTP
    // status is a final answer from the server and must never be queued.
    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers,
      });
    } catch {
      return {
        success: false,
        error: 'Network error',
        message: 'Không thể kết nối đến server',
      };
    }

    if (response.ok) {
      // A 2xx with an unreadable body is still accepted by the server.
      return { success: true, data: await response.json().catch(() => null) };
    }

    // 5xx: generic message, no retry (@ceo 04/10: a retry can create a duplicate punch).
    if (response.status >= 500) {
      return { success: false, error: `HTTP ${response.status}`, message: 'Lỗi server - vui lòng thử lại sau' };
    }

    if (response.status === 401) {
      return { success: false, error: 'Unauthorized', message: 'Phiên làm việc hết hạn - vui lòng đăng nhập lại' };
    }

    // 4xx refusal (409 ambiguous branch, 422 shift limit, 403 feature off, 400, 423...):
    // show the server's own message.
    const body = await response.json().catch(() => null);
    const fallback = response.status === 404 ? 'Không tìm thấy tài nguyên'
      : response.status === 422 ? 'Dữ liệu không hợp lệ'
      : 'Có lỗi xảy ra';
    return { success: false, error: `HTTP ${response.status}`, message: serverMessage(body) || fallback };
  }

  // Auth endpoints
  async login(username: string, password: string) {
    return this.makeRequest(API_ENDPOINTS.auth.login, {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  }

  async logout() {
    return this.makeRequest(API_ENDPOINTS.auth.logout, {
      method: 'POST',
    });
  }

  async getCurrentUser() {
    return this.makeRequest(API_ENDPOINTS.auth.me);
  }

  async refreshToken(refreshToken: string) {
    return this.makeRequest(API_ENDPOINTS.auth.refresh, {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  }

  // Attendance endpoints
  async getAttendanceStatus() {
    return this.makeRequest('/attendance/status');
  }

  async getAttendance() {
    return this.makeRequest(API_ENDPOINTS.attendance.list);
  }

  async getAttendanceHistory() {
    return this.makeRequest(API_ENDPOINTS.attendance.history);
  }

  // Per DD-0002 §Tier-3: each tenant_user is assigned to a branch_id
  // Per gps-attendance-api.yaml: clock-in requires branch_id (from user profile)
  async clockIn(latitude: number, longitude: number, photo?: string) {
    const branchId = this.getUserBranchId();
    return this.makeRequest(API_ENDPOINTS.attendance.clockIn, {
      method: 'POST',
      body: JSON.stringify({
        latitude,
        longitude,
        branch_id: branchId,
        photo: photo || undefined,
      }),
    });
  }

  /** #780: close a stale shift without GPS — tenant mode employee_then_review only (else 403). */
  async requestStaleClose(attendanceId: string, reason: string) {
    return this.makeRequest('/attendance/stale-close', {
      method: 'POST',
      body: JSON.stringify({ attendance_id: attendanceId, reason }),
    });
  }

  async clockOut(latitude: number, longitude: number, notes?: string) {
    const branchId = this.getUserBranchId();
    return this.makeRequest(API_ENDPOINTS.attendance.clockOut, {
      method: 'POST',
      body: JSON.stringify({ latitude, longitude, branch_id: branchId, notes }),
    });
  }

  /** Per DD-0002: branch_id stored in user profile at login */
  private getUserBranchId(): string | undefined {
    try {
      // Zustand store (auth-storage) → state.user.branch_id
      const zustand = localStorage.getItem('auth-storage');
      if (zustand) {
        const parsed = JSON.parse(zustand);
        const bid = parsed?.state?.user?.branch_id;
        if (bid) return bid;
      }
      // Fallback: AuthContext storage
      const authUser = localStorage.getItem('nqh_auth_user');
      if (authUser) {
        const bid = JSON.parse(authUser).branch_id;
        if (bid) return bid;
      }
    } catch { /* ignore parse errors */ }
    return undefined;
  }

  async getAttendanceStats(startDate?: string, endDate?: string) {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    return this.makeRequest(`${API_ENDPOINTS.attendance.stats}?${params}`);
  }

  // Schedule endpoints
  async getSchedules() {
    return this.makeRequest(API_ENDPOINTS.schedules.list);
  }

  async getMySchedule() {
    return this.makeRequest(API_ENDPOINTS.schedules.mySchedule);
  }

  // Task endpoints
  async getTasks() {
    return this.makeRequest(API_ENDPOINTS.tasks.list);
  }

  async getMyTasks() {
    return this.makeRequest(API_ENDPOINTS.tasks.myTasks);
  }

  async getPendingTasks() {
    return this.makeRequest(API_ENDPOINTS.tasks.pending);
  }

  async getCompletedTasks() {
    return this.makeRequest(API_ENDPOINTS.tasks.completed);
  }

  // User endpoints
  async getUsers() {
    if (!FEATURES.USER_MANAGEMENT_ENABLED) {
      return {
        success: false,
        error: 'Feature disabled',
        message: 'Quản lý người dùng tạm thời không khả dụng',
      };
    }
    return this.makeRequest(API_ENDPOINTS.users.list);
  }

  async getUserProfile() {
    return this.makeRequest(API_ENDPOINTS.users.profile);
  }

  async updateUserProfile(data: any) {
    return this.makeRequest(API_ENDPOINTS.users.update, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Organization endpoints
  async getOrganizations() {
    return this.makeRequest(API_ENDPOINTS.organizations.list);
  }

  async getOrganizationBranches() {
    return this.makeRequest(API_ENDPOINTS.organizations.branches);
  }

  // ZERO TOLERANCE POLICY: Real API endpoints only
  async getShifts() {
    if (!FEATURES.SHIFTS_ENABLED) {
      return {
        success: false,
        error: 'Feature disabled',
        message: 'Tính năng ca làm việc tạm thời không khả dụng',
      };
    }
    return this.makeRequest('/api/v1/shifts');
  }

  async getNotifications() {
    if (!FEATURES.NOTIFICATIONS_ENABLED) {
      return {
        success: false,
        error: 'Feature disabled', 
        message: 'Tính năng thông báo tạm thời không khả dụng',
      };
    }
    return this.makeRequest('/api/v1/notifications');
  }

  async getDepartments() {
    if (!FEATURES.DEPARTMENTS_ENABLED) {
      return {
        success: false,
        error: 'Feature disabled',
        message: 'Tính năng phòng ban tạm thời không khả dụng',
      };
    }
    return this.makeRequest('/api/v1/departments');
  }

  async getLocations() {
    if (!FEATURES.LOCATIONS_ENABLED) {
      return {
        success: false,
        error: 'Feature disabled',
        message: 'Tính năng địa điểm tạm thời không khả dụng',
      };
    }
    return this.makeRequest('/api/v1/locations');
  }

  async getDashboardMetrics() {
    return this.makeRequest('/api/v1/dashboard/metrics');
  }
}

// Export singleton instance
export const apiWrapper = new ApiWrapper();

// Export for use in stores
export default apiWrapper;